import * as R from 'Router/routes';
import { isProductKind } from 'utils/getProductNumber';
import { Link, Paragraph } from 'components';
import { memo } from 'react';
import { PRODUCT_NUMBER_IL } from 'utils/constants';
import { useLatestLoan } from 'hooks/webapi';
import { useLocation } from 'react-router-dom';
import { usePhone } from 'hooks/usePhone';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import EnvelopeIcon from 'assets/icons/envelope.svg?react';
import PhoneIcon from 'assets/icons/phone.svg?react';
import styles from './FooterDefault.module.scss';
import useBundle from 'hooks/useBundle';

export type Props = {
  className?: string;
  dataTest?: string;
};

export default memo(function FooterDefault({ className, dataTest }: Props) {
  const { t } = useTranslation('footer');
  const { isBundleAccepted } = useBundle();
  const { latestLoan } = useLatestLoan();
  const { pathname } = useLocation();

  const { phone, phoneLink } = usePhone();

  const showPaymentLink =
    isProductKind(latestLoan, PRODUCT_NUMBER_IL) &&
    [R.REPAYMENT, R.DR_PAYMENT_SCHEDULE].includes(pathname);

  const bundleStyle = isBundleAccepted && styles.bundle;

  return (
    <footer data-test={dataTest} className={classes(styles.footer, className)}>
      <div className={styles['first-container']}>
        <div className={styles.contacts}>
          <div className={styles.contact}>
            <EnvelopeIcon
              className={classes(styles.envelope, bundleStyle)}
              data-test={`${dataTest}-envelope-icon`}
            />
            <a
              data-test="footer-mail"
              href={`mailto:${t('common:email')}`}
              className={classes(styles.link, bundleStyle)}
              type="small"
            >
              {t('common:email')}
            </a>
          </div>
          <div className={styles.contact}>
            <PhoneIcon
              className={classes(styles.phone, bundleStyle)}
              data-test={`${dataTest}-phone-icon`}
            />
            <a
              data-test="footer-number"
              href={`tel:${phoneLink}`}
              className={classes(styles.link, bundleStyle)}
              type="small"
            >
              {phone}
            </a>
          </div>
        </div>
        <Paragraph
          className={styles['work-hours']}
          type="small"
          data-test={`${dataTest}-work-hours`}
        >
          {t('work_hours_weekdays')}
          <br />
          {t('work_hours_weekends')}
        </Paragraph>
      </div>
      <div
        className={classes([styles['second-container'], styles['helper-text']])}
      >
        {showPaymentLink && (
          <>
            <p
              className={classes([
                styles['helper-text'],
                styles['helper-text-link'],
              ])}
              data-test={`${dataTest}-helper-link-wrapper`}
            >
              <Link to={R.FULL_REPAYMENT} dataTest="full-repayment">
                {t('payFullLoan')}
              </Link>
            </p>
            <span className={styles['helper-separator']}>|</span>
          </>
        )}
        <p
          className={classes([
            styles['helper-text'],
            styles['helper-text-link'],
          ])}
          dangerouslySetInnerHTML={{
            __html: t('terms_and_conditions'),
          }}
          data-test={`${dataTest}-helper-terms-conditions`}
        />
      </div>
    </footer>
  );
});
